import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { BannerView, Category } from 'src/app/common/components/me-marketing-banner/me-marketing-banner-bean';
import { SidebarActivityType } from 'src/app/common/components/sidebar/sidebar';
import { InternalSettings, PermissionList } from 'src/app/common/services/internal-settings/internal-settings.interface';
import { hasOnePermission, InternalSettingsService } from 'src/app/common/services/internal-settings/internal-settings.service';
import { PAGroupUtility } from 'src/app/common/services/permitted-activity/pa-group.utility';
import { PAActivity, PAMenuMobile } from 'src/app/common/services/permitted-activity/pa-menu-meta';
import { PermittedActivityService } from 'src/app/common/services/permitted-activity/permitted-activity.service';
import { ResponsiveDesignService } from 'src/app/common/services/responsive-design/responsive-design.service';
import { VizAliasStatus } from 'src/app/common/services/viz/viz';
import { VizService } from 'src/app/common/services/viz/viz.service';
import { BiometricService } from 'src/app/system/utility/biometric.service';
import { LocalStorageService } from 'src/app/system/utility/local-storage.service';
import { NavigationService } from 'src/app/system/utility/navigation.service';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { UIUtilityService } from 'src/app/system/utility/ui-utility.service';

@Component({
    selector: 'app-available-activities',
    templateUrl: './available-activities.component.html',
    styleUrls: ['./available-activities.component.less'],
    standalone: false
})
export class AvailableActivitiesComponent implements OnInit {

  SidebarActivityType: any = SidebarActivityType;

  menu!: PAMenuMobile;
  PlatformService: any = PlatformService;
  UIUtilityService: any = UIUtilityService;
  BannerView: any = BannerView;

  title: string = '';
  category!: Category | null;
  activityList: PAActivity[] = [];
  is!: InternalSettings;

  constructor(
    private srvIS: InternalSettingsService,
    private permitted: PermittedActivityService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private srvLocalStorage: LocalStorageService,
    private srvBiometric: BiometricService,
    private srvRpDesign: ResponsiveDesignService,
    private srvViz: VizService,
  ) { }

  ngOnInit(): void {
    combineLatest([
      this.srvIS.getCurrentSettings(),
      this.activeRoute.queryParamMap,
      this.permitted.menuMobileApp$,
      this.permitted.menuMobileView$
    ]).subscribe(([is, params, menuMobileApp, menuMobileView]) => {
      this.is = is;
      // Switch On Bottom Nav when entering
      this.srvRpDesign.setBottomNav(true);

      this.menu = PlatformService.isMobile() ? menuMobileApp! : menuMobileView!;
      let module = params.get('module');
      if (module) {
        this.category = null;
        switch (module) {
          case ModulesWithAvailableActivityList.INTERNAL_TRANSFERS:
            this.title = "LABEL_INTERNAL_TRANSFERS";
            this.activityList = this.menu.getSubMenu('internalTransfers');
            this.category = Category.TRANSFER;
            break;

          case ModulesWithAvailableActivityList.EXTERNAL_TRANSFERS:
            this.title = "LABEL_EXTERNAL_TRANSFERS";
            this.activityList = this.menu.getSubMenu('externalTransfers');
            break;

          case ModulesWithAvailableActivityList.PAYMENTS:
            this.title = "LABEL_PAYMENTS";
            this.activityList = this.menu.getSubMenu('payments');
            this.category = Category.PAYMENT;
            break;

          case ModulesWithAvailableActivityList.QR_CODE_PAYMENT:
            this.title = "QR_PAYMENT.QR_CODE";
            this.activityList = this.menu.getSubMenu('qrcode');
            break;

          case ModulesWithAvailableActivityList.ORDERS:
            this.title = "ORDERS";
            this.activityList = this.menu.getSubMenu('orders');
            break;

          case ModulesWithAvailableActivityList.SERVICES:
            this.title = "LABEL_SERVICES";
            this.activityList = this.menu.getSubMenu('services');
            break;

          case ModulesWithAvailableActivityList.SETTINGS:
            this.title = "LABEL_SETTINGS";
            this.activityList = PAGroupUtility.buildSubMenuSettings(this.srvLocalStorage, this.srvBiometric);
            break;

          case ModulesWithAvailableActivityList.HELP_CENTER:
            this.title = "LABEL_HOW_CAN_WE_HELP";
            this.activityList = PAGroupUtility.buildSubMenuHelpCenter();
            break;

          case ModulesWithAvailableActivityList.EXTERNAL_LINKS:
            this.title = "LABEL_EXTERNAL_LINKS";
            this.activityList = this.menu.getSubMenu('externalLinks');
            break;

          default:
            break;
        }
      }
    })
  }

  protected navigate(activity: PAActivity) {
    if (!activity.path) return;
    if (activity.path.includes('viz')) {
      this.navigateToViz(activity);
      return;
    }
    this.router.navigateByUrl(activity.path);
  }

  protected navigateToDashboard() {
    NavigationService.gotoDashboard(this.router);
  }

  ngOnDestroy(): void {
    // Switch Off Bottom Nav when leaving
    this.srvRpDesign.setBottomNav(false);
  }

  navigateToViz(activity: PAActivity) {
    this.srvViz.getVizAliasInfo().subscribe((vizAliasInfo)=>{
      if (vizAliasInfo) {
        if (vizAliasInfo.status == VizAliasStatus.DISABLED) {
          if (hasOnePermission([PermissionList.ACCESS_DEACTIVATE_VIZ_ALIAS], this.is.permissions)) this.srvViz.showReviewDialog();
          else this.srvViz.showContactDialog();
          return;
        }
        combineLatest([this.srvViz.getVizCards(), this.srvViz.getPayeesList()]).subscribe(([cards, payees])=>{
            if(cards && payees) this.router.navigateByUrl(activity.path || '/dashboard');
        })
      } else {
        // May need something here
      }
    })
  }

}

export enum ModulesWithAvailableActivityList {
  INTERNAL_TRANSFERS = 'INTERNAL_TRANSFERS',
  EXTERNAL_TRANSFERS = 'EXTERNAL_TRANSFERS',
  PAYMENTS = 'PAYMENTS',
  ORDERS = 'ORDERS',
  SERVICES = 'SERVICES',
  QR_CODE_PAYMENT = 'QR_CODE_PAYMENT',
  EXTERNAL_LINKS = 'EXTERNAL_LINKS',
  SETTINGS = 'SETTINGS',
  HELP_CENTER = 'HELP_CENTER',
}